import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Loader2, Plus, PenLine, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, get, errMsg } from "@/lib/api";

const TYPES = { team: "Teamwork", conflict: "Conflict", leadership: "Leadership", change: "Change" };
const RANK = [[3, "Best"], [2, "Good"], [1, "Weak"], [0, "Worst"]];
const rid = () => Math.random().toString(36).slice(2, 10);
export const newScenario = () => ({ id: rid(), type: "team", competency: "", scenario: "", options: RANK.map(([score]) => ({ id: rid(), text: "", score })) });

function ScenarioCard({ sc, i, n, onChange, onMove, onRemove, canEdit }) {
  const tid = `sc-${sc.id}`;
  const scores = sc.options.map((o) => o.score);
  const badRank = [...scores].sort().join() !== "0,1,2,3";
  const setOpt = (j, patch) => onChange({ ...sc, options: sc.options.map((o, k) => (k === j ? { ...o, ...patch } : o)) });
  return (
    <li className="space-y-3 rounded-[6px] border border-line bg-white p-4" data-testid={tid}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">Scenario {i + 1}</span>
        <select value={sc.type} disabled={!canEdit} onChange={(e) => onChange({ ...sc, type: e.target.value })} className="h-8 rounded-lg border border-line bg-white px-2 text-sm" aria-label="Scenario type" data-testid={`${tid}-type`}>
          {Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Input value={sc.competency || ""} disabled={!canEdit} onChange={(e) => onChange({ ...sc, competency: e.target.value })} placeholder="Competency tag (optional)" className="h-8 w-56" aria-label="Competency tag" data-testid={`${tid}-competency`} />
        {canEdit && <span className="ml-auto flex gap-1">
          <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => onMove(-1)} aria-label="Move scenario up" data-testid={`${tid}-up`}><ArrowUp size={14} /></Button>
          <Button size="icon" variant="ghost" disabled={i === n - 1} onClick={() => onMove(1)} aria-label="Move scenario down" data-testid={`${tid}-down`}><ArrowDown size={14} /></Button>
          <Button size="icon" variant="ghost" onClick={onRemove} aria-label="Delete scenario" data-testid={`${tid}-remove`}><Trash2 size={14} /></Button>
        </span>}
      </div>
      <Textarea rows={3} value={sc.scenario} disabled={!canEdit} onChange={(e) => onChange({ ...sc, scenario: e.target.value })} placeholder="Describe the work situation" aria-label="Situation" data-testid={`${tid}-prompt`} />
      <fieldset className="space-y-2">
        <legend className="text-sm text-ink-secondary">Four possible responses, ranked from best to worst</legend>
        {sc.options.map((o, j) => (
          <div key={o.id} className="flex items-center gap-2">
            <select value={o.score} disabled={!canEdit} onChange={(e) => setOpt(j, { score: Number(e.target.value) })} className="h-8 w-24 rounded-lg border border-line bg-white px-2 text-sm" aria-label={`Rank for response ${j + 1}`} data-testid={`${tid}-rank-${j}`}>
              {RANK.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <Input value={o.text} disabled={!canEdit} onChange={(e) => setOpt(j, { text: e.target.value })} placeholder={`Response ${j + 1}`} className="h-8" data-testid={`${tid}-option-${j}`} />
          </div>
        ))}
        {badRank && <p className="text-xs text-caution" role="status" data-testid={`${tid}-bad-rank`}>Use each rank once: one Best, one Good, one Weak, one Worst.</p>}
      </fieldset>
    </li>
  );
}

export function AiDraft({ templateId, jobId, onDrafted }) {
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(jobId || "");
  const { data } = useQuery({ queryKey: ["jobs-min"], queryFn: () => get("/jobs"), enabled: !jobId && !!templateId });
  const jobs = Array.isArray(data) ? data : data?.items || [];
  const draft = async () => {
    setBusy(true);
    try { const r = await api.post(`/assessment-library/${templateId}/draft-scenarios`, { job_id: job || null }); onDrafted(r.data.scenarios); toast.success(`${r.data.scenarios.length} scenarios drafted. Review and edit them, then save.`); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (!templateId) return <p className="text-xs text-ink-muted">Save a draft first to use AI drafting.</p>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {!jobId && <select value={job} onChange={(e) => setJob(e.target.value)} className="h-8 rounded-lg border border-line bg-white px-2 text-sm" aria-label="Job description to use" data-testid="ai-draft-job">
        <option value="">Use the description of…</option>{jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}</select>}
      <Button size="sm" variant="outline" disabled={busy || (!jobId && !job)} onClick={draft} data-testid="ai-draft-scenarios">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <PenLine size={14} />} Draft with AI from the job description</Button>
    </div>
  );
}

export function ScenarioEditor({ s, onChange, canEdit, templateId, jobId }) {
  const list = s.scenarios;
  const set = (scenarios) => onChange({ ...s, scenarios });
  const move = (i, d) => { const n = [...list]; [n[i], n[i + d]] = [n[i + d], n[i]]; set(n); };
  return (
    <div className="space-y-3" data-testid="scenario-editor">
      {!list.length && <p className="text-sm text-ink-secondary">No scenarios in this assessment yet. When it's used for a job, the job's approved scenarios are used. Add your own or draft them with AI.</p>}
      <ol className="space-y-3">
        {list.map((sc, i) => <ScenarioCard key={sc.id} sc={sc} i={i} n={list.length} canEdit={canEdit} onChange={(x) => set(list.map((y, j) => (j === i ? x : y)))} onMove={(d) => move(i, d)} onRemove={() => set(list.filter((_, j) => j !== i))} />)}
      </ol>
      {canEdit && <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="outline" onClick={() => set([...list, newScenario()])} data-testid="add-scenario"><Plus size={14} /> Add scenario</Button>
        <AiDraft templateId={templateId} jobId={jobId} onDrafted={(x) => set([...list, ...x])} />
      </div>}
    </div>
  );
}
